import { ButtonGroup, ClickAwayListener, Grow, Popper, styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { useEffect, useRef, useState } from 'react';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import MenuItem from 'shell/components/MenuItem';
import MenuList from 'shell/components/MenuList';
import MUIPaper from 'shell/components/Paper';

const CustomButtonGroup = styled(ButtonGroup)(() => {
    return {
        gap: '1px',
    };
});

const LeftButton = styled(Button)(({ theme }) => {
    return {
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

const RightButton = styled(Button)(({ device, theme }) => {
    const width = {
        mobile: '2rem',
        tablet: '2.5rem',
        desktop: '3rem',
    }[device];

    return {
        margin: '0 !important',
        minWidth: 'unset !important',
        width,
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            outlineOffset: '-5px',
        },
    };
});

const CustomPaper = styled(MUIPaper)(({ theme }) => {
    return {
        borderRadius: '8px',
        boxShadow: theme.palette.ensightful.shadows[1],
    };
});

const SplitButton = ({ disabled, loading, options = [], onClick, ...rest }) => {
    if (!options.length) throw new Error('Shell: options cannot be empty in component SplitButton.');

    const { device } = useViewport();

    const anchorRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [selectedKey, setSelectedKey] = useState(options[0].key);

    const handleMenuItemClick = (index) => {
        setSelectedKey(index);
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const selectedOption = options.find(({ key }) => key === selectedKey);

    useEffect(() => {
        setSelectedKey(options[0].key);
    }, [options]);

    return (
        <>
            <CustomButtonGroup ref={anchorRef} aria-label={'split button'} disabled={disabled} {...rest}>
                <LeftButton
                    data-cy={'splitbutton-left'}
                    disabled={selectedOption?.disabled}
                    loading={loading}
                    onClick={() => onClick(selectedOption)}
                >
                    {selectedOption?.value}
                </LeftButton>
                <RightButton
                    data-cy={'splitbutton-right'}
                    aria-controls={open ? 'split-button-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-label={'select action'}
                    aria-haspopup={'menu'}
                    device={device}
                    onClick={handleToggle}
                >
                    <Icon sx={{ fontSize: '1rem' }} kind={open ? 'chevronDown' : 'chevronUp'} />
                </RightButton>
            </CustomButtonGroup>
            <Popper
                anchorEl={anchorRef.current}
                disablePortal
                open={open}
                placement={'top-end'}
                role={undefined}
                transition
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                        <CustomPaper elevation={0}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id={'split-button-menu'} autoFocusItem>
                                    {options.map((option) => (
                                        <MenuItem
                                            key={option.key}
                                            disabled={option.disabled}
                                            selected={selectedKey === option.key}
                                            onClick={() => handleMenuItemClick(option.key)}
                                        >
                                            {option.value}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </CustomPaper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

export default SplitButton;
